import {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useCallback,
  useImperativeHandle,
} from "react";
import {
  Col,
  Row,
  OutlinedButton,
  Button,
  NMWaitIndicator,
  AppErrorBoundary,
} from "@neumetric/blueprint-lib/components";
import {
  getCurrentUser,
  getCurrentModule,
} from "@neumetric/blueprint-lib/store";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Soc2Type2Report } from "../Soc2Type2";
import {
  getCurrentAudit,
  auditActions,
  getCurrentReportDetails,
} from "../../../store";

const AuditReportHeader = (props) => {
  const currentAudit = useSelector(getCurrentAudit);

  return (
    <Col className="w-100 card-shadow pl-20 pr-20 pb-10 bg-white">
      <Link className="mt-4 mb-12" to={-1}>
        <span className="fs-14 fw-400 text-action">Back</span>
      </Link>
      <Row className="w-100">
        <Col className="flex-auto">
          <Row className="w-100" justify="between" align="center">
            <Row align="center">
              <span className="fs-20 fw-600 text-heading">
                {currentAudit.title}
              </span>
            </Row>
            {props.allowEdit && (
              <Row align="center">
                {props.isEditMode ? (
                  <>
                    <OutlinedButton
                      label="Cancel"
                      icon="CANCEL"
                      variant="CAUTION"
                      disabled={props.submitting}
                      classes={{ button: "mr-25" }}
                      onClick={props.onCancel}
                    />
                    <Button
                      label="Save"
                      icon="SAVE"
                      loading={props.submitting}
                      classes={{ button: "mr-25" }}
                      onClick={props.onSave}
                    />
                  </>
                ) : (
                  <Button
                    label="Edit"
                    icon="EDIT"
                    classes={{ button: "mr-25" }}
                    onClick={props.onEdit}
                  />
                )}
              </Row>
            )}
          </Row>
        </Col>
      </Row>
    </Col>
  );
};

export const AuditReport = forwardRef((props, ref) => {
  const { asmtId, allowEdit, showHeader, onSaveComplete } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const soc2Type2ReportRef = useRef(null);
  const [state, setState] = useState({
    isEditMode: false,
    submitting: false,
    reloading: false,
  });
  const currentUser = useSelector(getCurrentUser);
  const currentAudit = useSelector(getCurrentAudit);
  const currentModule = useSelector(getCurrentModule);
  const auditReport = useSelector(getCurrentReportDetails);

  const loadAudit = () => {
    const action = auditActions.fetchAudit({
      auditId: asmtId,
      skipControl: false,
    });
    action.onFailure = (error) => {
      if (error.status === 404) {
        navigate("/page-not-found");
      } else if (error.status === 403) {
        navigate("/restricted");
      }
    };
    action.onComplete = (res) => {
      fetchAuditReport(res);
    };
    dispatch(action);
  };

  const fetchAuditReport = (audit) => {
    const asmt = audit || currentAudit;
    if (asmt.ent_report) {
      const action = auditActions.fetchReportShow({
        report_id: asmt.ent_report.id,
      });
      dispatch(action);
    }
  };

  useEffect(() => {
    if (asmtId) {
      if (currentAudit) {
        fetchAuditReport(currentAudit);
      } else {
        loadAudit();
      }
    }
  }, [asmtId]);

  const updateSoc2Type2Report = (data, iOptions) => {
    const options = iOptions || {};
    data.status = auditReport.status;
    const action = auditActions.updateAuditReport({
      ent_report: data,
      id: auditReport.id,
    });
    action.onComplete = options.onComplete;
    action.onFailure = options.onFailure;
    dispatch(action);
  };

  const reloadSoc2Type2Report = useCallback(() => {
    dispatch(auditActions.setCurrentReportDetails(null));
    fetchAuditReport(currentAudit);
  }, [currentAudit]);

  const handleEdit = () => {
    setState((_) => ({ ..._, isEditMode: true }));
    soc2Type2ReportRef.current.setMode("edit");
  };

  const handleCancel = () => {
    setState((_) => ({ ..._, reloading: true, isEditMode: false }));
    const timer = setTimeout(() => {
      clearTimeout(timer);
      setState((_) => ({ ..._, reloading: false }));
    }, 500);
  };

  const handleSaveReport = () => {
    const updatedData = soc2Type2ReportRef.current.getData();
    setState((_) => ({ submitting: true }));
    const options = {
      onComplete: () => {
        setState((_) => ({ submitting: false, isEditMode: false }));
        onSaveComplete && onSaveComplete();
        soc2Type2ReportRef.current.setMode("view");
        reloadSoc2Type2Report();
      },
      onFailure: () => {
        setState((_) => ({ submitting: false }));
      },
    };
    updateSoc2Type2Report(updatedData, options);
  };

  useImperativeHandle(ref, () => ({
    onEdit: handleEdit,
    onCancel: handleCancel,
    onSave: handleSaveReport,
    getContentForExport: () => {
      return soc2Type2ReportRef.current.getContentForExport();
    },
  }));

  return (
    <Col
      className={`w-100 h-100 overflow-hide audit-view audit-report-view ${state.isEditMode ? "edit-mode" : "view-mode"}`}
    >
      <AppErrorBoundary>
        {!currentAudit ? (
          <NMWaitIndicator size="small" />
        ) : (
          <>
            {showHeader && (
              <AuditReportHeader
                allowEdit={allowEdit && Boolean(auditReport)}
                isEditMode={state.isEditMode}
                submitting={state.submitting}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSave={handleSaveReport}
              />
            )}
            <Col className="w-100 flex-auto overflow-hide">
              {!currentAudit.ent_report ||
              (currentAudit.ent_report &&
                auditReport &&
                auditReport.content === null) ? (
                <Col className="w-100 h-100" align="center" justify="center">
                  No Active Blueprint to generate the Report
                </Col>
              ) : auditReport && !state.reloading ? (
                <Soc2Type2Report
                  ref={soc2Type2ReportRef}
                  initialMode={state.isEditMode ? "edit" : "view"}
                  reportData={auditReport}
                  currentAudit={currentAudit}
                  updateReport={updateSoc2Type2Report}
                  reloadReport={reloadSoc2Type2Report}
                  currentUser={currentUser}
                  currentModule={currentModule}
                />
              ) : (
                <NMWaitIndicator size="small" />
              )}
            </Col>
          </>
        )}
      </AppErrorBoundary>
    </Col>
  );
});
